import { sdk } from '../sdk';

// Normalize SDK/HTTP client responses to a plain payload object.
// Some endpoints appear to return AxiosResponse while others return the payload directly.
// This helper ensures we always work with the payload.
function getPayload<T = any>(response: any): T {
  if (response && typeof response === 'object' && 'data' in response) {
    // AxiosResponse-like
    return (response as any).data as T;
  }
  return response as T;
}

export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  fullName?: string; // Server sends this field
  email: string;
  phone?: string;
  cnic?: string;
  role: 'SuperAdmin' | 'Admin' | 'Cashier';
  isActive: boolean;
  officeId?: {
    _id: string;
    name: string;
    officeNo: number;
  };
  regionId?: {
    _id: string;
    name: string;
    regionId: number;
  };
  features?: Record<string, boolean>;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  cnic?: string;
  password: string;
  role: 'SuperAdmin' | 'Admin' | 'Cashier';
  officeId?: string;
  regionId?: string;
  features?: Record<string, boolean>;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  cnic?: string;
  password?: string;
  role?: 'SuperAdmin' | 'Admin' | 'Cashier';
  officeId?: string;
  regionId?: string;
  features?: Record<string, boolean>;
  isActive?: boolean;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserResponse {
  user: User;
}

export interface MessageResponse {
  message: string;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
}

export class UsersAPI {
  // Get all users with pagination and filters
  static async list(params: ListUsersParams = {}): Promise<UsersResponse> {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.search) searchParams.append('search', params.search);
      if (params.role) searchParams.append('role', params.role);
      if (params.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());

      const url = `/api/v1/users?${searchParams.toString()}`;
      
      const response = await sdk.client.get(url);
      // Some clients return AxiosResponse, others return payload directly
      const responseData = getPayload<UsersResponse>(response);
      
      // Check if the response has the expected structure
      if (!responseData.users || !responseData.pagination) {
        throw new Error('Invalid response structure from server');
      }
      
      // Return the data directly since the SDK already stripped the wrapper
      return {
        users: responseData.users,
        pagination: responseData.pagination
      };
    } catch (error: any) {
      throw error;
    }
  }

  // Get single user by ID
  static async get(id: string): Promise<UserResponse> {
    const response = await sdk.client.get(`/api/v1/users/${id}`);
    const responseData = getPayload<UserResponse>(response);
    
    if (!responseData.user) {
      throw new Error('Failed to fetch user');
    }
    
    return { user: responseData.user };
  }

  // Create new user
  static async create(userData: CreateUserData): Promise<UserResponse> {
    const response = await sdk.client.post('/api/v1/users', userData);
    const responseData = getPayload<UserResponse>(response);
    
    if (!responseData.user) {
      throw new Error('Failed to create user');
    }
    
    return { user: responseData.user };
  }

  // Update user
  static async update(id: string, userData: UpdateUserData): Promise<UserResponse> {
    const response = await sdk.client.put(`/api/v1/users/${id}`, userData);
    const responseData = getPayload<UserResponse>(response);
    
    if (!responseData.user) {
      throw new Error('Failed to update user');
    }
    
    return { user: responseData.user };
  }

  // Delete user (soft delete)
  static async delete(id: string): Promise<MessageResponse> {
    const response = await sdk.client.delete(`/api/v1/users/${id}`);
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData || !responseData.message) {
      throw new Error('Failed to delete user');
    }
    
    return { message: responseData.message };
  }

  // Update user features
  static async updateFeatures(id: string, features: Record<string, boolean>): Promise<MessageResponse> {
    const response = await sdk.client.post(`/api/v1/users/${id}/features`, { features });
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData.message) {
      throw new Error('Failed to update user features');
    }
    
    return { message: responseData.message };
  }

  // Activate user
  static async activate(id: string): Promise<MessageResponse> {
    const response = await sdk.client.post(`/api/v1/users/${id}/activate`);
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData || !responseData.message) {
      throw new Error('Failed to activate user');
    }
    
    return { message: responseData.message };
  }

  // Deactivate user
  static async deactivate(id: string): Promise<MessageResponse> {
    const response = await sdk.client.post(`/api/v1/users/${id}/deactivate`);
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData || !responseData.message) {
      throw new Error('Failed to deactivate user');
    }
    
    return { message: responseData.message };
  }

  // Reset user password
  static async resetPassword(id: string, newPassword: string): Promise<MessageResponse> {
    const response = await sdk.client.post(`/api/v1/users/${id}/reset-password`, { newPassword });
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData.message) {
      throw new Error('Failed to reset user password');
    }
    
    return { message: responseData.message };
  }

  // Get current user profile
  static async getProfile(): Promise<UserResponse> {
    const response = await sdk.client.get('/api/v1/users/profile');
    const responseData = getPayload<UserResponse>(response);
    
    if (!responseData.user) {
      throw new Error('Failed to fetch profile');
    }
    
    return { user: responseData.user };
  }

  // Update current user profile
  static async updateProfile(userData: Partial<UpdateUserData>): Promise<UserResponse> {
    const response = await sdk.client.put('/api/v1/users/profile', userData);
    const responseData = getPayload<UserResponse>(response);
    
    if (!responseData.user) {
      throw new Error('Failed to update profile');
    }
    
    return { user: responseData.user };
  }

  // Change current user password
  static async changePassword(currentPassword: string, newPassword: string): Promise<MessageResponse> {
    const response = await sdk.client.put('/api/v1/users/profile/password', {
      currentPassword,
      newPassword
    });
    const responseData = getPayload<MessageResponse>(response);
    
    if (!responseData.message) {
      throw new Error('Failed to change password');
    }
    
    return { message: responseData.message };
  }
}
